import React, { useState, useRef, useEffect } from "react";

export default function AdharVarificationStep({ setCurrentStep, selectedOffer }) {
  const [aadhaar, setAadhaar] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);
  
  // Flow States
  const [isOtpRequested, setIsOtpRequested] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  
  const [addressChoice, setAddressChoice] = useState("yes");

  // Starts empty, ready for your eKYC API payload
  const [ekycAddressDetails, setEkycAddressDetails] = useState({
    line1: "",
    line2: "",
    pincode: ""
  });

  // Single editable state for the input boxes (starts strictly empty)
  const [currentAddress, setCurrentAddress] = useState({
    line1: "",
    line2: "",
    pincode: ""
  });

  // Handle countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timerId = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timerId);
    }
  }, [timeLeft]);

  const handleGetOtp = () => {
    console.log("Requesting OTP from UIDAI for:", aadhaar);
    setIsOtpRequested(true);
    setTimeLeft(60); 
  };

  const handleResendOtp = () => {
    if (timeLeft === 0) {
      console.log("Resending OTP...");
      setTimeLeft(60);
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0].focus();
    }
  };

  const handleVerifyAadhaar = () => {
    const fetchedApiData = {
      line1: "", 
      line2: "",
      pincode: ""
    };
    
    setEkycAddressDetails(fetchedApiData);
    setCurrentAddress({ ...fetchedApiData });
    setIsVerified(true);
  };

  const handleSaveAndContinue = () => {
    let finalAddressToSave = `${currentAddress.line1}, ${currentAddress.line2}, ${currentAddress.pincode}`;
    console.log("Saving Address:", finalAddressToSave);
    setCurrentStep(10);
  };

  const handleRadioChange = (choice) => {
    setAddressChoice(choice);
    if (choice === "yes") {
      setCurrentAddress({ ...ekycAddressDetails });
    } else {
      setCurrentAddress({ line1: "", line2: "", pincode: "" });
    }
  };

  const handleAddressChange = (e) => {
    setCurrentAddress({ ...currentAddress, [e.target.name]: e.target.value });
  };

  const handleAadhaarChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value.length <= 12) setAadhaar(value);
  };

  const handleOtpChange = (index, e) => {
    const value = e.target.value;
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    if (value !== "" && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && otp[index] === "" && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  // Format Aadhaar for display with double spaces for wider aesthetic spreading
  const formattedAadhaar = aadhaar.replace(/(.{4})/g, "$1  ").trim();
  const formatTime = (seconds) => `00:${seconds < 10 ? `0${seconds}` : seconds}`;
  
  const lenderName = selectedOffer?.lender_name || "IDFC FIRST Bank";

  return (
    <div className="w-full max-w-[400px] mx-auto bg-white min-h-[100dvh] font-atkinson flex flex-col relative pb-[140px]">

      {/* Header & Progress Bar */}
      <div className="px-5 pt-6 pb-4 border-b border-transparent">
        <div className="flex justify-center mb-4">
          <div className="text-center leading-none">
            <h1 className="text-[#205A6D] font-bold text-[22px] tracking-tight">
              {lenderName}
            </h1>
            <p className="text-[#7ea4b1] text-[10px] font-bold tracking-widest mt-1.5 uppercase">Application Process</p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button 
            onClick={() => {
              if (isVerified) { setIsVerified(false); }
              else if (isOtpRequested) { setIsOtpRequested(false); setOtp(["", "", "", "", "", ""]); }
              else { setCurrentStep(8); }
            }} 
            className="w-6 h-6 flex items-center justify-start text-gray-700 hover:opacity-70 transition-opacity"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          </button>
          <div className="flex-1 mx-4 h-[5px] bg-[#E8F6EF] rounded-full overflow-hidden">
            <div className="h-full bg-[#00C676] w-[36%] rounded-full transition-all duration-500"></div>
          </div>
          <span className="text-[12px] text-[#8b8b8b] font-semibold">36%</span>
        </div>
      </div>

      <div className="px-5 pt-2 flex-1">
        <h2 className="text-[22px] font-semibold text-[#1b1b1b] tracking-tight">Aadhaar verification</h2>
        
        {/* 👉 Adjusted spacing: Changed mb-3.5 to mb-5 for the perfect middle-ground gap */}
        <p className="text-[13px] text-[#979797] font-normal leading-tight mt-1.5 mb-5 pr-2">
          Enter your 12-digit Aadhaar. We'll verify it via UIDAI OTP.
        </p>

        {/* Aadhaar Input */}
        <div className={`border rounded-[14px] w-full px-4 pt-2.5 pb-2.5 bg-white transition-colors duration-300 ${isVerified || isOtpRequested ? 'border-[#e6e6e6] bg-[#FAFAFA]' : 'border-[#e6e6e6] focus-within:border-black'}`}>
          <label className="text-[11px] text-[#8b8b8b] block mb-[2px] font-medium">Aadhaar Number</label>
          <input
            type="text"
            placeholder="1 2 3 4  5 6 7 8  9 0 0 0"
            className="w-full outline-none text-[16px] tracking-[0.2em] text-[#1b1b1b] bg-transparent placeholder-[#cccccc] font-normal [&::-webkit-search-cancel-button]:appearance-none"
            value={formattedAadhaar}
            onChange={handleAadhaarChange}
            maxLength="18"
            disabled={isOtpRequested || isVerified}
          />
        </div>
        
        {/* Disclaimer - Centered beneath the input box */}
        <p className="text-[10px] text-[#b3b3b3] mt-2 text-center font-medium">Your Aadhaar is never stored in plain text.</p>

        {/* OTP Section (Appears after clicking Get OTP) */}
        {isOtpRequested && !isVerified && (
          <div className="mt-8 animate-in fade-in slide-in-from-top-4 duration-500">
            <label className="text-[13px] text-[#1b1b1b] block mb-2.5 font-bold">Enter UIDAI OTP</label>
            <div className="flex justify-between gap-2">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  className="w-[46px] h-[50px] border border-[#e5e5e5] rounded-[10px] text-center text-[20px] font-bold text-[#1b1b1b] focus:border-black focus:outline-none transition-colors shadow-sm"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                />
              ))}
            </div>
            
            <div className="flex justify-between items-center mt-4 px-1 text-[12px]">
              <span className="text-[#8b8b8b] font-medium">
                OTP valid for <span className="font-bold text-[#1b1b1b]">{formatTime(timeLeft)}</span>
              </span>
              <button 
                onClick={handleResendOtp}
                disabled={timeLeft > 0}
                className={`font-bold transition-colors ${timeLeft > 0 ? 'text-[#cccccc] cursor-not-allowed' : 'text-[#FF7A00] hover:text-[#e66e00]'}`}
              >
                Resend OTP
              </button>
            </div>
          </div>
        )}

        {/* VERIFIED SECTION (Address confirmation) */}
        {isVerified && (
          <div className="mt-6 animate-in fade-in slide-in-from-top-4 duration-500">

            {/* COMPACT Green Success Box */}
            <div className="bg-[#EBFBF4] border border-[#D1EFE2] rounded-[16px] p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-4 h-4 rounded-full bg-[#00C676] flex items-center justify-center">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                <div className="text-[11px] font-bold text-[#35614C] tracking-wider uppercase">
                  Aadhaar Verified Via UIDAI
                </div>
              </div>
              
              <div className="flex flex-col gap-2 text-[13px]">
                <div className="flex justify-between">
                  <span className="text-[#6b6b6b] font-medium">Name</span>
                  <span className="font-bold text-[#1b1b1b] text-right"></span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#6b6b6b] font-medium">DOB</span>
                  <span className="font-bold text-[#1b1b1b] text-right"></span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#6b6b6b] font-medium">Gender</span>
                  <span className="font-bold text-[#1b1b1b] text-right"></span>
                </div>
                <div className="flex justify-between pt-1">
                  <span className="text-[#6b6b6b] font-medium flex-none">Address</span>
                  <span className="font-bold text-[#1b1b1b] text-right leading-relaxed max-w-[200px]">
                    {ekycAddressDetails.line1 && `${ekycAddressDetails.line1},`}
                    {ekycAddressDetails.line1 && <br />}
                    {ekycAddressDetails.line2} {ekycAddressDetails.pincode && `- ${ekycAddressDetails.pincode}`}
                  </span>
                </div>
              </div>
            </div>

            {/* Address Confirmation */}
            <div className="mt-8">
              <p className="font-bold text-[14px] text-[#1b1b1b] mb-4 tracking-tight">
                Is your current address the same as permanent?
              </p>

              <div className="flex flex-col gap-3">
                {/* Radio 1: Yes */}
                <label className={`flex items-center gap-3 py-3.5 px-4 rounded-[14px] border-[1.5px] ${addressChoice === "yes" ? "border-black bg-[#FAFAFA]" : "border-[#e5e5e5] bg-white"} cursor-pointer transition-all`}>
                  <div className={`w-4 h-4 rounded-full border-[1.5px] flex items-center justify-center flex-none ${addressChoice === "yes" ? "border-black" : "border-[#ccc]"}`}>
                    {addressChoice === "yes" && <div className="w-2 h-2 bg-black rounded-full"></div>}
                  </div>
                  <input type="radio" name="address" value="yes" className="hidden" checked={addressChoice === "yes"} onChange={() => handleRadioChange("yes")} />
                  <span className="text-[14px] text-[#1b1b1b] font-medium">Yes, same as Aadhaar address</span>
                </label>

                {/* Radio 2: No */}
                <label className={`flex items-center gap-3 py-3.5 px-4 rounded-[14px] border-[1.5px] ${addressChoice === "no" ? "border-black bg-[#FAFAFA]" : "border-[#e5e5e5] bg-white"} cursor-pointer transition-all`}>
                  <div className={`w-4 h-4 rounded-full border-[1.5px] flex items-center justify-center flex-none ${addressChoice === "no" ? "border-black" : "border-[#ccc]"}`}>
                    {addressChoice === "no" && <div className="w-2 h-2 bg-black rounded-full"></div>}
                  </div>
                  <input type="radio" name="address" value="no" className="hidden" checked={addressChoice === "no"} onChange={() => handleRadioChange("no")} />
                  <span className="text-[14px] text-[#1b1b1b] font-medium">No, I'll enter a different address</span>
                </label>
              </div>

              {/* ADDRESS INPUTS */}
              <div className="flex flex-col gap-3 mt-5 animate-in fade-in slide-in-from-top-2 duration-300">
                
                <div className="border border-[#e6e6e6] rounded-[14px] px-4 py-3 bg-white focus-within:border-black transition-colors">
                  <label className="text-[11px] text-[#8b8b8b] block mb-0.5 font-medium">Address-Line 1</label>
                  <input
                    type="text"
                    name="line1"
                    value={currentAddress.line1}
                    onChange={handleAddressChange}
                    placeholder="Floor, Building Name, Street Name"
                    className="w-full outline-none text-[15px] text-[#1b1b1b] bg-transparent placeholder-[#cccccc] font-medium mt-0.5"
                  />
                </div>

                <div className="border border-[#e6e6e6] rounded-[14px] px-4 py-3 bg-white focus-within:border-black transition-colors">
                  <label className="text-[11px] text-[#8b8b8b] block mb-0.5 font-medium">Address-Line 2 (optional)</label>
                  <input
                    type="text"
                    name="line2"
                    value={currentAddress.line2}
                    onChange={handleAddressChange}
                    placeholder="Landmark, City"
                    className="w-full outline-none text-[15px] text-[#1b1b1b] bg-transparent placeholder-[#cccccc] font-medium mt-0.5"
                  />
                </div>

                <div className="border border-[#e6e6e6] rounded-[14px] px-4 py-3 bg-white focus-within:border-black transition-colors">
                  <label className="text-[11px] text-[#8b8b8b] block mb-0.5 font-medium">Home Pin Code</label>
                  <input
                    type="text"
                    name="pincode"
                    value={currentAddress.pincode}
                    onChange={handleAddressChange}
                    inputMode="numeric"
                    maxLength="6"
                    placeholder="Enter Pincode"
                    className="w-full outline-none text-[15px] text-[#1b1b1b] bg-transparent placeholder-[#cccccc] font-medium mt-0.5"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sticky Bottom Button Logic */}
      <div className="fixed bottom-0 left-0 right-0 z-50 mx-auto w-full max-w-[400px] bg-gradient-to-t from-white via-white to-transparent px-5 pt-8 pb-5">
        
        {/* State 1: Request OTP */}
        {!isOtpRequested && (
          <button
            onClick={handleGetOtp}
            disabled={aadhaar.length !== 12}
            className="w-full bg-black text-white font-bold text-[17px] rounded-[16px] py-[18px] active:scale-[0.98] transition-transform disabled:opacity-30 disabled:cursor-not-allowed shadow-lg shadow-black/10"
          >
            Get OTP
          </button>
        )}

        {/* State 2: Verify OTP */}
        {isOtpRequested && !isVerified && (
          <button
            onClick={handleVerifyAadhaar}
            disabled={otp.join("").length !== 6}
            className="w-full bg-black text-white font-bold text-[17px] rounded-[16px] py-[18px] active:scale-[0.98] transition-transform disabled:opacity-30 disabled:cursor-not-allowed shadow-lg shadow-black/10"
          >
            Verify Aadhaar
          </button>
        )}

        {/* State 3: Save Address */}
        {isVerified && (
          <button
            onClick={handleSaveAndContinue}
            className="w-full bg-black text-white font-bold text-[17px] rounded-[16px] py-[18px] active:scale-[0.98] transition-transform shadow-lg shadow-black/10"
          >
            Save & Continue
          </button>
        )}
      </div>
    </div>
  );
}